/* eslint-disable @typescript-eslint/no-explicit-any */
import type { MouseEvent, ReactElement, ReactNode } from "react";
import type { IconName, IndicatorProps } from "@huub-web/ui";

export type HotspotRecord = {
  id: string;
  lat: number;
  lng: number;
  title?: string;
  subtitle?: ReactNode;
  category?: string;
  indicatorVariant?: IndicatorProps["variant"];
  delay?: number;
  type?: "marker" | "dot" | "cluster" | "poi";
  tooltip?: ReactNode;
  icon?: IconName | ReactElement<any>;
  tooltipHideDelay?: number;
  onClick?: (e: MouseEvent<HTMLElement>) => void;
  /** If true, this hotspot will not be included in clustering */
  noClustering?: boolean;
  /** Z-index for layering hotspots (higher = on top) */
  zIndex?: number;
  /** If true, shows a close button on the tooltip card */
  closable?: boolean;
  /** Whether this hotspot is in active state */
  active?: boolean;
  /** Callback when close button is clicked */
  onClose?: () => void;
};

/**
 * Creates marker elements for hotspots on the map
 * @param map - Mapbox map instance
 * @param Marker - Mapbox Marker constructor
 * @param hotspots - Array of hotspot data
 * @returns Array of marker instances
 */
export const createHotspotMarkers = (
  map: any,
  Marker: any,
  hotspots: HotspotRecord[],
): any[] => {
  const markers: any[] = [];

  // Check if map is ready and has a container before adding markers
  // Also check _container which is used internally by Mapbox for DOM operations
  if (!map || !map.getContainer || !map.getContainer() || !map._container) {
    return markers;
  }

  hotspots.forEach((hotspot) => {
    try {
      const el = document.createElement("div");
      el.id = hotspot.id;
      el.style.width = "0";
      el.style.height = "0";

      const marker = new Marker({
        element: el,
        anchor: "center",
      })
        .setLngLat([hotspot.lng, hotspot.lat])
        .addTo(map);

      // Apply z-index to marker element if specified
      if (hotspot.zIndex !== undefined) {
        const markerEl = marker.getElement();
        if (markerEl) {
          markerEl.style.zIndex = String(hotspot.zIndex);
        }
      }

      markers.push(marker);
    } catch (error) {
      // Silently handle marker creation errors - map may not be fully ready
      console.warn(`Failed to create marker for hotspot ${hotspot.id}:`, error);
    }
  });

  return markers;
};

/**
 * Removes all markers from the map
 * @param markers - Array of marker instances to remove
 */
export const clearMarkers = (markers: any[]) => {
  markers.forEach((marker) => {
    marker.remove();
  });
};
